/** Parse Firebase Auth email-action URLs handled in-app. */

export type AuthActionMode = 'resetPassword' | 'recoverEmail' | 'verifyEmail';

export const AUTH_ACTION_MODES: readonly AuthActionMode[] = [
  'resetPassword',
  'recoverEmail',
  'verifyEmail',
];

export type AuthActionParams = {
  mode: AuthActionMode | string;
  oobCode: string;
  apiKey?: string;
  lang?: string;
  continueUrl?: string;
};

export function isAuthActionMode(mode: string): mode is AuthActionMode {
  return (AUTH_ACTION_MODES as readonly string[]).includes(mode);
}

export function isAuthActionPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  return path === '/auth/action';
}

export function parseAuthActionSearch(search: string): AuthActionParams | null {
  const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  const mode = String(params.get('mode') ?? '').trim();
  const oobCode = String(params.get('oobCode') ?? '').trim();
  if (!mode || !oobCode) return null;
  return {
    mode,
    oobCode,
    apiKey: params.get('apiKey') ?? undefined,
    lang: params.get('lang') ?? undefined,
    continueUrl: params.get('continueUrl') ?? undefined,
  };
}

/** Rebuild invite / reset link onto the SPA auth action handler. */
export function toAppAuthActionUrl(firebaseLink: string, appOrigin: string): string {
  const source = new URL(firebaseLink);
  const origin = appOrigin.replace(/\/+$/, '');
  const next = new URL(`${origin}/auth/action`);
  for (const key of ['mode', 'oobCode', 'apiKey', 'lang', 'continueUrl'] as const) {
    const value = source.searchParams.get(key);
    if (value) next.searchParams.set(key, value);
  }
  return next.toString();
}

/** Only allow continue redirects that stay on the app origin. */
export function safeSameOriginContinueUrl(
  continueUrl: string | undefined,
  appOrigin: string,
): string | null {
  const raw = String(continueUrl ?? '').trim();
  if (!raw) return null;
  try {
    const target = new URL(raw);
    const origin = new URL(appOrigin.replace(/\/+$/, '') || 'https://invalid.local');
    if (target.origin !== origin.origin) return null;
    return target.toString();
  } catch {
    return null;
  }
}
